import { globalConfirm } from '../components/ConfirmDialog';
import { useFormatter } from '../utils/formatters';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { LoadingSpinner, EmptyState } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import TypeAheadLookup from '../components/TypeAheadLookup';
import { SOStatusBadge } from './SalesOrdersPage';
import api, { getApiErrorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, X, Plus, Trash2, Save,
  Activity, StickyNote, Paperclip, Package, Upload, FileText
} from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../components/SortableTableHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EDITABLE_STATUSES = new Set(['DRAFT', 'REJECTED']);
const SHIPPING_METHODS = ['Standard Delivery','Express Delivery','Overnight Delivery','Same Day Delivery','Click & Collect','Freight / Pallet','Air Freight','Sea Freight','Courier (DHL)','Courier (FedEx)','Courier (UPS)','Royal Mail / Post','Digital Delivery'];
const INCOTERMS = ['EXW','FCA','CPT','CIP','DAP','DDP','FAS','FOB','CFR','CIF'];
const PAYMENT_TERMS_LIST = ['Net 7','Net 14','Net 30','Net 45','Net 60','Net 90','Due on Receipt','50% Upfront','Monthly','Quarterly','Annual'];

const fmtWithTz = (d, tz) => { if (!d) return ''; try { return new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric', timeZone: tz}); } catch { return d; }  };
const fmtTsWithTz = (d, tz) => { if (!d) return ''; try { return new Date(d).toLocaleString('en-GB',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit', timeZone: tz}); } catch { return d; } };

const PANEL_TABS = [
  { id:'lines',   icon:Package,   label:'Line Items',  badgeKey:'line_items' },
  { id:'notes',   icon:StickyNote,label:'Notes',       badgeKey:'notes' },
  { id:'attach',  icon:Paperclip, label:'Attachments', badgeKey:'attachments' },
  { id:'events',  icon:Activity,  label:'Event Log',   badgeKey:null },
];

function IconSidebarSO({ active, onSelect, counts }) {
  return (
    <div className="flex flex-col border-r bg-muted/10 w-10 flex-shrink-0">
      {PANEL_TABS.map(tab => {
        const count = counts?.[tab.badgeKey] || 0;
        const isActive = active === tab.id;
        return (
          <button key={tab.id} onClick={() => onSelect(tab.id)} title={tab.label}
            className={`relative flex items-center justify-center h-10 w-10 border-b transition-colors ${
              isActive ? 'bg-primary/10 border-l-2 border-l-primary' : 'hover:bg-accent'
            }`}>
            <tab.icon className={`h-4 w-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
            {count > 0 && (
              <span className="absolute top-0.5 right-0.5 min-w-[14px] h-3.5 rounded-full bg-primary text-white text-[9px] flex items-center justify-center px-0.5">{count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function LineItemForm({ onSave, onCancel }) {
  const [form, setForm] = useState({ item_code:'',item_description:'',quantity:1,unit:'EACH',unit_price:0,discount:0,tax_rate:0,required_date:'' });
  const f = (k,v) => setForm(p=>({...p,[k]:v}));
  const net = parseFloat(((parseFloat(form.quantity)||0)*(parseFloat(form.unit_price)||0)*(1-(parseFloat(form.discount)||0)/100)).toFixed(2));
  const tax = parseFloat((net*(parseFloat(form.tax_rate)||0)/100).toFixed(2));
  return (
    <div className="border rounded-xl p-4 bg-muted/20 space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1"><Label className="text-xs">Item Code</Label><Input value={form.item_code} onChange={e=>f('item_code',e.target.value)} className="h-8 text-sm" /></div>
        <div className="col-span-2 space-y-1"><Label className="text-xs">Item Description *</Label>
          <TypeAheadLookup endpoint="/api/data/items" valueField="item_code" labelField="outline"
            placeholder="Search items..." onChange={(code,rec) => setForm(p=>({...p,item_code:code,item_description:rec?.outline||p.item_description}))} />
        </div>
        {form.item_description === '' && <div className="col-span-3 space-y-1"><Label className="text-xs">Free-text Description</Label><Input value={form.item_description} onChange={e=>f('item_description',e.target.value)} className="h-8 text-sm" placeholder="Or type a description" /></div>}
        <div className="space-y-1"><Label className="text-xs">Quantity</Label><Input type="number" step="0.01" value={form.quantity} onChange={e=>f('quantity',e.target.value)} className="h-8" /></div>
        <div className="space-y-1"><Label className="text-xs">Unit</Label><Input value={form.unit} onChange={e=>f('unit',e.target.value)} className="h-8" /></div>
        <div className="space-y-1"><Label className="text-xs">Unit Price</Label><Input type="number" step="0.01" value={form.unit_price} onChange={e=>f('unit_price',e.target.value)} className="h-8" /></div>
        <div className="space-y-1"><Label className="text-xs">Discount %</Label><Input type="number" step="0.01" value={form.discount} onChange={e=>f('discount',e.target.value)} className="h-8" /></div>
        <div className="space-y-1"><Label className="text-xs">Tax Rate %</Label><Input type="number" step="0.01" value={form.tax_rate} onChange={e=>f('tax_rate',e.target.value)} className="h-8" /></div>
        <div className="space-y-1"><Label className="text-xs">Required Date</Label><Input type="date" value={form.required_date} onChange={e=>f('required_date',e.target.value)} className="h-8" /></div>
      </div>
      <div className="flex items-center justify-between">
        <div className="text-xs space-x-4">
          <span>Net: <strong className="text-primary">{net.toLocaleString('en-US',{minimumFractionDigits:2})}</strong></span>
          <span>Tax: <strong>{tax.toLocaleString('en-US',{minimumFractionDigits:2})}</strong></span>
          <span>Gross: <strong>{(net+tax).toLocaleString('en-US',{minimumFractionDigits:2})}</strong></span>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button size="sm" onClick={() => onSave({...form,quantity:parseFloat(form.quantity)||0,unit_price:parseFloat(form.unit_price)||0,discount:parseFloat(form.discount)||0,tax_rate:parseFloat(form.tax_rate)||0})} disabled={!form.item_description}>Add Line</Button>
        </div>
      </div>
    </div>
  );
}

export default function SalesOrderDetailPage() {
  const { soNumber } = useParams();
  const navigate = useNavigate();
  const { user, systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const fmt = d => fmtWithTz(d, tz);
  const fmtTs = d => fmtTsWithTz(d, tz);
  const [so, setSo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [headerEdit, setHeaderEdit] = useState({});
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [activePanel, setActivePanel] = useState('lines');
  const [addingLine, setAddingLine] = useState(false);
  const [events, setEvents] = useState([]);
  const [soEvtSort, handleSoEvtSort] = useSortState('event_date', 'desc');
  const [newNote, setNewNote] = useState({ notes:'', privacy:'Private' });
  const [actionLoading, setActionLoading] = useState(false);
  const docRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get(`/api/sales-orders/${soNumber}`); setSo(res.data); setHeaderEdit(res.data); }
    catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load'); }
    setLoading(false);
  }, [soNumber]);

  const loadEvents = useCallback(async () => {
    try { const r = await api.get(`/api/sales-orders/${soNumber}/events`); setEvents(r.data); } catch {}
  }, [soNumber]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (activePanel === 'events') loadEvents(); }, [activePanel, loadEvents]);

  const canEdit = so && EDITABLE_STATUSES.has(so.status);
  const h = (k,v) => setHeaderEdit(p=>({...p,[k]:v}));

  const saveHeader = async () => {
    setSaving(true);
    try { await api.put(`/api/sales-orders/${soNumber}`, headerEdit); toast.success('Saved'); load(); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
    setSaving(false);
  };

  const addLine = async (item) => {
    try { await api.post(`/api/sales-orders/${soNumber}/line-items`, item); toast.success('Line added'); setAddingLine(false); load(); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  const deleteLine = async (id) => {
    if (!await globalConfirm('Delete this line?')) return;
    try { await api.delete(`/api/sales-orders/${soNumber}/line-items/${id}`); load(); } catch (err) { if (err?.response?.status >= 500) toast.error('Failed'); }
  };

  const addNote = async () => {
    if (!newNote.notes.trim()) return;
    try { await api.post(`/api/sales-orders/${soNumber}/notes`, newNote); setNewNote({notes:'',privacy:'Private'}); load(); toast.success('Note added'); }
    catch (err) { if (err?.response?.status >= 500) toast.error('Failed'); }
  };

  const doAction = async (endpoint, msg, extraData={}) => {
    setActionLoading(true);
    try { await api.post(`/api/sales-orders/${soNumber}/${endpoint}`, extraData); toast.success(msg); load(); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Action failed')); }
    setActionLoading(false);
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file); fd.append('description', '');
    try { await api.post(`/api/requisitions/${soNumber}/attachments`, fd); toast.success('Uploaded'); load(); }
    catch (err) { if (err?.response?.status >= 500) toast.error('Upload failed'); }
  };

  if (loading) return <AppShell><div className="py-24"><LoadingSpinner size="lg" /></div></AppShell>;
  if (!so) return <AppShell><EmptyState title="Sales order not found" /></AppShell>;

  const netTotal = so.order_net_total || 0;
  const taxTotal = so.order_tax_total || 0;
  const grossTotal = so.order_gross_total || 0;
  const counts = so.counts || {};

  return (
    <AppShell>
      {/* Blue header bar matching the requisition style */}
      <div className="-mx-4 md:-mx-6 lg:-mx-8 -mt-6 px-4 md:px-6 py-3 mb-4 border-b" style={{backgroundColor:'#dce8f5'}}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-bold text-base">Sales Order</span>
            <SOStatusBadge status={so.status} />
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => navigate('/sales-orders')} aria-label="Back"><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" aria-label="Next"><ChevronRight className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" onClick={() => navigate('/sales-orders')} className="text-red-600"><X className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="grid grid-cols-6 gap-3 mt-2">
          {[['SO Number',so.so_number],['Status',so.status],['Order Date',fmt(so.order_date)],['Customer',so.customer_name],['Salesperson',so.salesperson],['Currency',so.currency]].map(([l,v])=>(
            <div key={l}><p className="text-[10px] text-muted-foreground">{l}</p><p className="text-sm font-semibold">{v||'—'}</p></div>
          ))}
        </div>
      </div>

      {/* Header card */}
      <div className="border rounded-xl mb-4">
        <button onClick={()=>setHeaderCollapsed(v=>!v)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/30 rounded-t-xl text-sm font-semibold">
          <span>Order Header</span>
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-primary">Net {netTotal.toLocaleString('en-US',{minimumFractionDigits:2})} | Tax {taxTotal.toLocaleString('en-US',{minimumFractionDigits:2})} | Gross {grossTotal.toLocaleString('en-US',{minimumFractionDigits:2})} ({so.currency})</span>
            <ChevronLeft className={`h-4 w-4 transition-transform ${headerCollapsed ? '-rotate-90' : 'rotate-90'}`} />
          </div>
        </button>
        {!headerCollapsed && (
          <div className="px-4 py-4">
            <div className="grid grid-cols-4 gap-4 mb-3">
              <div className="space-y-1">
                <p className="text-xs text-red-500 font-medium">Customer Code</p>
                {canEdit ? (
                  <TypeAheadLookup endpoint="/api/customers" valueField="customer_code" labelField="customer_name"
                    value={headerEdit.customer_code} displayValue={headerEdit.customer_code}
                    placeholder="Search customers..." data-testid="so-customer-lookup"
                    onChange={(code, rec) => setHeaderEdit(p=>({...p, customer_code:code, customer_name:rec?.customer_name||p.customer_name, payment_terms:rec?.payment_terms||p.payment_terms, currency:rec?.currency||p.currency, ship_to_address:rec?.address||p.ship_to_address, ship_to_city:rec?.city||p.ship_to_city, ship_to_country:rec?.country||p.ship_to_country}))}
                  />
                ) : <p className="text-sm">{so.customer_code||'—'}</p>}
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Customer Name</p>
                <Input value={headerEdit.customer_name||''} readOnly className="bg-muted/40 text-sm h-9" />
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Description</p>
                {canEdit ? <Input value={headerEdit.description||''} onChange={e=>h('description',e.target.value)} className="h-9 text-sm" /> : <p className="text-sm">{so.description||'—'}</p>}
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Customer PO Ref</p>
                {canEdit ? <Input value={headerEdit.ref_number||''} onChange={e=>h('ref_number',e.target.value)} className="h-9 text-sm" /> : <p className="text-sm">{so.ref_number||'—'}</p>}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 mb-3">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Required Date</p>
                {canEdit ? <Input type="date" value={headerEdit.required_date?.split('T')[0]||''} onChange={e=>h('required_date',e.target.value)} className="h-9" /> : <p className="text-sm">{fmt(so.required_date)||'—'}</p>}
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Shipping Method</p>
                {canEdit ? (
                  <select value={headerEdit.shipping_method||''} onChange={e=>h('shipping_method',e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                    <option value="">-- Select --</option>
                    {SHIPPING_METHODS.map(s=><option key={s}>{s}</option>)}
                  </select>
                ) : <p className="text-sm">{so.shipping_method||'—'}</p>}
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Incoterms</p>
                {canEdit ? (
                  <select value={headerEdit.incoterms||''} onChange={e=>h('incoterms',e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                    <option value="">-- Select --</option>
                    {INCOTERMS.map(i=><option key={i}>{i}</option>)}
                  </select>
                ) : <p className="text-sm">{so.incoterms||'—'}</p>}
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Payment Terms</p>
                {canEdit ? (
                  <select value={headerEdit.payment_terms||''} onChange={e=>h('payment_terms',e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                    {PAYMENT_TERMS_LIST.map(p=><option key={p}>{p}</option>)}
                  </select>
                ) : <p className="text-sm">{so.payment_terms||'—'}</p>}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Ship To Address</p>
                {canEdit ? <Input value={headerEdit.ship_to_address||''} onChange={e=>h('ship_to_address',e.target.value)} className="h-9 text-sm" /> : <p className="text-sm">{so.ship_to_address||'—'}</p>}
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Ship To City</p>
                {canEdit ? <Input value={headerEdit.ship_to_city||''} onChange={e=>h('ship_to_city',e.target.value)} className="h-9 text-sm" /> : <p className="text-sm">{so.ship_to_city||'—'}</p>}
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Ship To Country</p>
                {canEdit ? <Input value={headerEdit.ship_to_country||''} onChange={e=>h('ship_to_country',e.target.value)} className="h-9 text-sm" /> : <p className="text-sm">{so.ship_to_country||'—'}</p>}
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Tracking Number</p>
                <Input value={headerEdit.tracking_number||''} onChange={e=>h('tracking_number',e.target.value)} className="h-9 text-sm" />
              </div>
              <div className="col-span-4 space-y-1">
                <p className="text-xs text-muted-foreground">Special Instructions</p>
                {canEdit ? <Textarea value={headerEdit.special_instructions||''} onChange={e=>h('special_instructions',e.target.value)} rows={2} className="text-sm" /> : <p className="text-sm">{so.special_instructions||'—'}</p>}
              </div>
            </div>

            {/* Action buttons */}
            <div className="mt-4 pt-4 border-t flex flex-wrap gap-2">
              {canEdit && <Button size="sm" onClick={saveHeader} disabled={saving}><Save className="h-4 w-4 mr-1" />{saving?'Saving...':'Save Header'}</Button>}
              {canEdit && <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={()=>doAction('submit','Submitted for approval')} disabled={actionLoading}>Submit</Button>}
              {so.status==='SUBMITTED' && <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={()=>doAction('approve','Approved')} disabled={actionLoading}>Approve</Button>}
              {so.status==='APPROVED'  && <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={()=>{ const tn=window.prompt('Enter tracking number (optional):',''); doAction('dispatch','Dispatched',{tracking_number:tn||''}); }} disabled={actionLoading}>Dispatch</Button>}
              {so.status==='DISPATCHED'&& <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={()=>doAction('invoice','Invoiced')} disabled={actionLoading}>Invoice</Button>}
              {so.status==='INVOICED'  && <Button size="sm" onClick={()=>doAction('close','Closed')} disabled={actionLoading}>Close Order</Button>}
              {!['CLOSED','CANCELLED'].includes(so.status) && <Button size="sm" variant="destructive" onClick={()=>{ const r=window.prompt('Cancellation reason:',''); if(r)doAction('cancel',`Cancelled`,{reason:r}); }} disabled={actionLoading}>Cancel</Button>}
            </div>
          </div>
        )}
      </div>

      {/* Panels */}
      <div className="flex border rounded-xl overflow-hidden min-h-[360px]">
        <IconSidebarSO active={activePanel} onSelect={setActivePanel} counts={counts} />
        <div className="flex-1 p-4 overflow-auto">

          {/* Line Items */}
          {activePanel==='lines' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-primary">Line Items</h3>
                {canEdit && <Button size="sm" onClick={()=>setAddingLine(true)}><Plus className="h-4 w-4 mr-1" />Add Item</Button>}
              </div>
              {addingLine && <div className="mb-3"><LineItemForm onSave={addLine} onCancel={()=>setAddingLine(false)} /></div>}
              <table className="w-full text-xs border-collapse">
                <thead><tr className="bg-muted/50 border-b">
                  {['#','Item Code','Description','Qty','Unit','Unit Price','Disc%','Tax%','Net','Tax','Gross','Req Date',''].map(h2=>(
                    <th key={h2} className="text-left px-2 py-2 font-semibold text-muted-foreground border-b">{h2}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(so.line_items||[]).map(item=>(
                    <tr key={item.id} className="border-b hover:bg-accent/30">
                      <td className="px-2 py-2">{item.item_num}</td>
                      <td className="px-2 py-2 font-mono text-primary">{item.item_code}</td>
                      <td className="px-2 py-2 max-w-[200px] truncate font-medium">{item.item_description}</td>
                      <td className="px-2 py-2 text-right">{item.quantity}</td>
                      <td className="px-2 py-2">{item.unit}</td>
                      <td className="px-2 py-2 text-right">{item.unit_price?.toLocaleString('en-US',{minimumFractionDigits:2})}</td>
                      <td className="px-2 py-2 text-right">{item.discount?.toFixed(2)}%</td>
                      <td className="px-2 py-2 text-right">{item.tax_rate?.toFixed(2)}%</td>
                      <td className="px-2 py-2 text-right font-semibold">{item.net_price?.toLocaleString('en-US',{minimumFractionDigits:2})}</td>
                      <td className="px-2 py-2 text-right">{item.tax_amount?.toLocaleString('en-US',{minimumFractionDigits:2})}</td>
                      <td className="px-2 py-2 text-right font-bold">{item.gross_price?.toLocaleString('en-US',{minimumFractionDigits:2})}</td>
                      <td className="px-2 py-2">{fmt(item.required_date)}</td>
                      <td className="px-2 py-2">{canEdit && <button onClick={()=>deleteLine(item.id)} className="text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr className="border-t font-semibold bg-muted/30">
                  <td colSpan={8} className="px-2 py-2 text-sm">Totals ({(so.line_items||[]).length} items)</td>
                  <td className="px-2 py-2 text-right">{netTotal.toLocaleString('en-US',{minimumFractionDigits:2})}</td>
                  <td className="px-2 py-2 text-right">{taxTotal.toLocaleString('en-US',{minimumFractionDigits:2})}</td>
                  <td className="px-2 py-2 text-right text-primary">{grossTotal.toLocaleString('en-US',{minimumFractionDigits:2})}</td>
                  <td colSpan={2}></td>
                </tr></tfoot>
              </table>
            </div>
          )}

          {/* Notes */}
          {activePanel==='notes' && (
            <div>
              <h3 className="font-bold text-primary mb-3">Notes</h3>
              <div className="flex items-end gap-2 mb-4">
                <div className="flex-1 space-y-1"><Label className="text-xs">Note</Label><Textarea value={newNote.notes} onChange={e=>setNewNote(p=>({...p,notes:e.target.value}))} rows={2} placeholder="Add a note..." /></div>
                <div className="space-y-1"><Label className="text-xs">Privacy</Label>
                  <select value={newNote.privacy} onChange={e=>setNewNote(p=>({...p,privacy:e.target.value}))} className="h-9 rounded-lg border border-input bg-background px-2 text-sm"><option>Private</option><option>Public</option></select>
                </div>
                <Button size="sm" onClick={addNote} disabled={!newNote.notes.trim()}>Add</Button>
              </div>
              <table className="w-full text-sm"><thead><tr className="bg-muted/50 border-b">
                <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">By</th><th className="text-left px-3 py-2">Privacy</th><th className="text-left px-3 py-2">Note</th>
              </tr></thead><tbody>{(so.notes||[]).map(n=>(
                <tr key={n.id} className="border-b"><td className="px-3 py-2 text-muted-foreground">{fmtTs(n.entry_date)}</td><td className="px-3 py-2">{n.entered_by}</td><td className="px-3 py-2">{n.privacy}</td><td className="px-3 py-2">{n.notes}</td></tr>
              ))}</tbody></table>
            </div>
          )}

          {/* Attachments */}
          {activePanel==='attach' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-primary">Attachments</h3>
                <><input type="file" id="so-file" className="hidden" ref={docRef} onChange={handleDocUpload} />
                <Button size="sm" variant="outline" onClick={()=>docRef.current?.click()}><Upload className="h-4 w-4 mr-1" />Attach</Button></>
              </div>
              {(so.attachments||[]).length===0 ? <EmptyState title="No attachments" /> : (
                <table className="w-full text-sm"><thead><tr className="bg-muted/50 border-b">
                  <th className="text-left px-3 py-2">File</th><th className="text-left px-3 py-2">Description</th><th className="text-left px-3 py-2">Date</th>
                </tr></thead><tbody>{so.attachments.map((a,i)=>(
                  <tr key={i} className="border-b"><td className="px-3 py-2 text-primary">{a.file_name}</td><td className="px-3 py-2">{a.description}</td><td className="px-3 py-2 text-muted-foreground">{fmtTs(a.entry_date)}</td></tr>
                ))}</tbody></table>
              )}
            </div>
          )}

          {/* Event Log */}
          {activePanel==='events' && (
            <div>
              <h3 className="font-bold text-primary mb-3">Event Log</h3>
              {events.length===0 ? <EmptyState title="No events" /> : (
                <table className="w-full text-xs"><thead><tr className="bg-muted/50 border-b">
                  <SortableTableHeader label="Date" field="event_date" sortState={soEvtSort} onSort={handleSoEvtSort} />
                  <SortableTableHeader label="User" field="user" sortState={soEvtSort} onSort={handleSoEvtSort} />
                  <SortableTableHeader label="Event" field="event" sortState={soEvtSort} onSort={handleSoEvtSort} />
                </tr></thead><tbody>{sortData(events, soEvtSort.field, soEvtSort.dir).map((e,i)=>(
                  <tr key={i} className="border-b hover:bg-accent/20">
                    <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">{fmtTs(e.event_date)}</td>
                    <td className="px-3 py-1.5">{e.user}</td><td className="px-3 py-1.5">{e.event}</td>
                  </tr>
                ))}</tbody></table>
              )}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
